// Early opportunities are adjacent battles; the seeded winner then climbs the
// remaining positions. A grid changes the path to victory, never the winner.
// Fifty stories: the ten families below, each with five variants that differ in
// how many passes the pack makes and how tightly it runs.
const definitions = [
  ['recovery', [2, 1, 0], 8, false],
  ['counterattack', [0, 0, 1, 0], 1.2, true],
  ['braking', [1, 0, 2], 6, false],
  ['corner-duel', [0, 0, 0, 0], .45, true],
  ['breakthrough', [3, 2, 1, 0], 7, false],
  ['sprint-duel', [1, 0, 1, 0], .7, true],
  ['comeback', [0, 2, 3], 8, false],
  ['three-way', [0, 1, 0], .16, true],
  ['lead-relay', [0, 1, 0, 1], 3, false],
  ['photo-finish', [1, 0, 0], .18, true],
  ['recovery', [1, 0], 1.2, false],
  ['recovery', [2, 1, 0, 1, 0], 1.2, false],
  ['recovery', [1, 0], 6, false],
  ['recovery', [2, 1, 0, 1, 0, 1], 6, false],
  ['counterattack', [0, 1], 1.2, true],
  ['counterattack', [0, 1, 0, 1, 0], 1.2, true],
  ['counterattack', [1, 0], 6, true],
  ['counterattack', [1, 0, 1, 0, 1], 6, true],
  ['braking', [2, 1], 1.2, false],
  ['braking', [2, 1, 0, 1, 0], 1.6, false],
  ['braking', [1, 2], 6, false],
  ['braking', [3, 2, 1, 0, 1], 6, false],
  ['corner-duel', [0, 0], 1.2, true],
  ['corner-duel', [0, 1, 0, 1, 0], 1.5, true],
  ['corner-duel', [0, 1], 6, true],
  ['corner-duel', [1, 0, 1, 2, 1], 6, true],
  ['breakthrough', [3, 2], 1.2, false],
  ['breakthrough', [3, 2, 1, 0, 1, 0], 1.2, false],
  ['breakthrough', [2, 3], 6, false],
  ['breakthrough', [3, 2, 1, 0, 1, 0, 1], 6, false],
  ['sprint-duel', [1, 0], 1.2, true],
  ['sprint-duel', [1, 0, 1, 0, 1], 1.2, true],
  ['sprint-duel', [0, 1], 5.5, true],
  ['sprint-duel', [1, 2, 1, 0, 1, 0], 6, true],
  ['comeback', [2, 3], 1.2, false],
  ['comeback', [3, 2, 1, 0, 1], 1.2, false],
  ['comeback', [1, 2], 5.5, false],
  ['comeback', [3, 2, 1, 0, 1, 0, 1], 5.5, false],
  ['three-way', [0, 1, 0], 1.2, true],
  ['three-way', [0, 1, 0, 1, 0], 1.4, true],
  ['three-way', [1, 0, 1], 6, true],
  ['three-way', [0, 1, 0, 1, 0, 1], 6, true],
  ['lead-relay', [0, 1], 1.2, false],
  ['lead-relay', [0, 1, 0, 1, 0], 1.2, false],
  ['lead-relay', [1, 0], 5.5, false],
  ['lead-relay', [0, 1, 0, 1, 0, 1, 0], 6, false],
  ['photo-finish', [1, 0], .2, true],
  ['photo-finish', [1, 0, 1, 0, 1], .2, true],
  ['photo-finish', [2, 1, 0], 6, true],
  ['photo-finish', [1, 2, 1, 0, 1, 0], 5.5, true],
];

export const FLOW_STORIES = Object.freeze(definitions.map(([family, attacks, gap, finalDuel], index) => Object.freeze({
  id: `s${String(index + 1).padStart(2, '0')}`, family, duration: 40,
  attacks: Object.freeze(attacks), gap, finalDuel, threeWay: family === 'three-way',
})));

export function planBattles(round) {
  const story = FLOW_STORIES[round.scenarioIndex];
  const order = [...round.gridOrder], operations = [];
  const swap = rank => {
    operations.push({ rank, attacker: order[rank + 1], defender: order[rank] });
    [order[rank], order[rank + 1]] = [order[rank + 1], order[rank]];
  };
  story.attacks.forEach(swap);
  const target = story.finalDuel ? 1 : 0;
  if (target === 1 && order[0] === round.winnerId) swap(0);
  while (order.indexOf(round.winnerId) > target) swap(order.indexOf(round.winnerId) - 1);
  const interval = ((story.finalDuel ? 34.4 : 36.3) - 5.8) / operations.length;
  const actions = operations.map((action, index) => ({ ...action,
    start: 5.8 + index * interval, end: 5.8 + index * interval + Math.min(3.6, interval - .16),
    side: (index + round.scenarioIndex) % 2 ? -1 : 1,
    id: `${story.id}:pass:${index}`, kind: action.rank === 0 ? 'straightAttack' : 'recovery',
  }));
  if (round.raceVersion >= 6) {
    // Reserve the whole linked corner for one duel. Other passes fit around
    // the braking zone and exit instead of cutting across the S arbitrarily.
    const cornerIndex = actions.length <= 3 ? 1 : 2;
    const cornerEnd = 21;
    const afterCount = actions.length - cornerIndex - 1;
    const afterSpan = afterCount ? ((story.finalDuel ? 34.4 : 36.3) - (cornerEnd + .3)) / afterCount : 0;
    actions.forEach((action, index) => {
      if (index < cornerIndex) {
        const braking = index === cornerIndex - 1;
        action.start = braking ? 8.55 : 5.8;
        action.end = braking ? 11.3 : 8.3;
        action.tactic = braking ? 'inside-brake' : 'slipstream';
        if (braking) action.side = 1;
      } else if (index === cornerIndex) {
        action.start = 11.6; action.end = cornerEnd;
        action.tactic = ['counterattack', 'corner-duel', 'comeback', 'photo-finish'].includes(story.family) ? 'switchback' : 's-duel';
        action.side = action.tactic === 'switchback' ? 1 : -1;
      } else {
        action.start = cornerEnd + .3 + (index-cornerIndex-1)*afterSpan;
        action.end = action.start + Math.min(4.2, afterSpan-.2);
        action.tactic = 'slipstream';
      }
    });
  }
  const closingOrder = [...order];
  if (story.finalDuel) [order[0], order[1]] = [order[1], order[0]];
  return { story, actions, closingOrder, finalOrder: order };
}
